import { SUPPLIER_BACKEND_API_ENDPOINTS } from "../config/apiConfig";
import { apiClient } from "./api-client.service";

import type {
  UpdateFullInventoryPayload,
  UpdateInventoryPayload,
  AddNewSeatPayload,
  DecreaseExitingSeatPayload,
  InventoryCreatePayload,
  InventoryFilter,
  InventoryDetailResponse,
  InventoryMutationResponse,
  InventoryResponse,
  UpdateExitingFarePayload,
} from "../types/inventories.types";

const endpoints = SUPPLIER_BACKEND_API_ENDPOINTS.inventory;

export const inventoriesService = {
  async list(filter: InventoryFilter): Promise<InventoryResponse> {
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      });

      return await apiClient<InventoryResponse>(endpoints.list(params.toString()), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return {
        success: false,
        statusCode: 500,
        message: error instanceof Error ? error.message : "Failed to load inventory due to an unexpected error.",
        resData: null,
      } as unknown as InventoryResponse;
    }
  },

  async details(id: number | string): Promise<InventoryDetailResponse> {
    try {
      return await apiClient<InventoryDetailResponse>(endpoints.details(id), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return {
        success: false,
        statusCode: 500,
        message: error instanceof Error ? error.message : "Failed to load inventory details due to an unexpected error.",
        resData: null,
      } as unknown as InventoryDetailResponse;
    }
  },

  async updateExistingFare(payload: UpdateExitingFarePayload): Promise<InventoryMutationResponse> {
    try {
      return await apiClient<InventoryMutationResponse>(endpoints.updateExitingFare, {
        method: "POST",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return {
        success: false,
        statusCode: 500,
        message: error instanceof Error ? error.message : "Failed to update fare due to an unexpected error.",
        resData: null,
      };
    }
  },

  async addNewSeat(payload: AddNewSeatPayload): Promise<InventoryMutationResponse> {
    try {
      return await apiClient<InventoryMutationResponse>(endpoints.addNewSeat, {
        method: "POST",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return {
        success: false,
        statusCode: 500,
        message: error instanceof Error ? error.message : "Failed to add seats due to an unexpected error.",
        resData: null,
      };
    }
  },

  async decreaseExistingSeat(payload: DecreaseExitingSeatPayload): Promise<InventoryMutationResponse> {
    try {
      return await apiClient<InventoryMutationResponse>(endpoints.decreaseExitingSeat, {
        method: "POST",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return {
        success: false,
        statusCode: 500,
        message: error instanceof Error ? error.message : "Failed to decrease seats due to an unexpected error.",
        resData: null,
      };
    }
  },

  async updateInventoryDetail(payload: UpdateInventoryPayload): Promise<InventoryMutationResponse> {
    try {
      return await apiClient<InventoryMutationResponse>(endpoints.updateInventoryDetail, {
        method: "POST",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return {
        success: false,
        statusCode: 500,
        message: error instanceof Error ? error.message : "Failed to update inventory due to an unexpected error.",
        resData: null,
      };
    }
  },

  async delete(id: number | string): Promise<InventoryMutationResponse> {
    try {
      return await apiClient<InventoryMutationResponse>(endpoints.delete(id), {
        method: "DELETE",
        isAuthenticated: true,
      });
    } catch (error) {
      return {
        success: false,
        statusCode: 500,
        message: error instanceof Error ? error.message : "Failed to delete inventory due to an unexpected error.",
        resData: null,
      };
    }
  },

  // only the changed fields (e.g. { status }) are sent
  async update(id: number | string, payload: Partial<UpdateFullInventoryPayload>): Promise<InventoryMutationResponse> {
    try {
      return await apiClient<InventoryMutationResponse>(endpoints.update(id), {
        method: "PATCH",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return {
        success: false,
        statusCode: 500,
        message: error instanceof Error ? error.message : "Failed to update inventory due to an unexpected error.",
        resData: null,
      };
    }
  },

  async create(payload: InventoryCreatePayload): Promise<InventoryMutationResponse> {
    try {
      return await apiClient<InventoryMutationResponse>(endpoints.create, {
        method: "POST",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return {
        success: false,
        statusCode: 500,
        message: error instanceof Error ? error.message : "Failed to create inventory due to an unexpected error.",
        resData: null,
      };
    }
  },
};
